import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../../utils/motionVariants";
import Classes from "./Benefits.module.css";

const benefits = [
    {
        title: "Prevents Fires",
        desc:
            "The U.S. Fire Administration reports nearly 3,000 dryer fires every year, resulting in injuries, deaths, and millions in property damage.",
        icon: "/assets/icons/flame.png",
        accent: "primary",
    },
    {
        title: "Lowers Energy Bills",
        desc:
            "Cleaning your dryer vent yearly can help lower energy costs by up to $288, saving money each year while also keeping your home safe today.",
        icon: "/assets/icons/energy.png",
        accent: "default",
    },
    {
        title: "Increases Lifespan",
        desc:
            "Maintaining your dryer vent improves performance, boosts efficiency, and helps extend the life of your dryer over time.",
        icon: "/assets/icons/love.png",
        accent: "default",
    },
    {
        title: "Protects Your Clothes",
        desc:
            "When dryer vents become clogged, your dryer overheats, exposing your clothes to excessive heat and increasing the risk of fabric damage.",
        icon: "/assets/icons/tshirt.png",
        accent: "default",
    },
    {
        title: "Reduces Allergens",
        desc:
            "Regular dryer vent cleaning ensures proper moisture release and helps prevent mold, indoor allergen buildup, and vent leaks.",
        icon: "/assets/icons/dust.png",
        accent: "default",
    },
    {
        title: "Reduces Repairs",
        desc:
            "Regular dryer vent cleaning improves efficiency, reduces wasted energy, and helps you save money on utility costs every month.",
        icon: "/assets/icons/support.png",
        accent: "default",
    },
];

export default function Benefits() {
    // same approach: block animations until user scrolls once
    const [scrollStarted, setScrollStarted] = useState(false);

    useEffect(() => {
        const setIfScrolled = () => {
            if (window.scrollY > 0) {
                setScrollStarted(true);
                return true;
            }
            return false;
        };

        // handle refresh / restored scroll position
        if (setIfScrolled()) return;

        const onScroll = () => {
            if (setIfScrolled()) window.removeEventListener("scroll", onScroll);
        };

        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.section
            id="benefits"
            className={Classes.section}
            variants={stagger}
            initial="hidden"
            animate={scrollStarted ? "visible" : "hidden"}
            // keep in-view behavior but only after scroll has started
            {...(scrollStarted
                ? { whileInView: "visible", viewport: { once: true, amount: 0.2 } }
                : {})}
        >
            <div className={Classes.inner}>
                <motion.div variants={fadeUp(0)} className={Classes.kicker}>
                    Benefits of Dryer Vent Cleaning
                </motion.div>

                <motion.h2 variants={fadeUp(0.1)} className={Classes.heading}>
                    Why Dryer Vent Cleaning Matters
                </motion.h2>

                <motion.p variants={fadeUp(0.2)} className={Classes.subtext}>
                    From fire prevention to faster drying times, clean vents protect your
                    home and save you money.
                </motion.p>

                <motion.div className={Classes.grid} variants={stagger}>
                    {benefits.map((b, i) => (
                        <motion.article
                            key={b.title}
                            className={`${Classes.card} ${Classes.cardDefault}`}
                            variants={fadeUp(i * 0.12)}
                            whileHover={{ y: -6 }}
                            transition={{
                                type: "spring",
                                stiffness: 90,
                                damping: 16,
                                mass: 0.7,
                            }}
                        >
                            <div className={Classes.cardContent}>
                                <h3 className={Classes.cardTitle}>{b.title}</h3>
                                <p className={Classes.cardDesc}>{b.desc}</p>
                            </div>

                            {/* watermark icon */}
                            <div className={Classes.watermarkWrap} aria-hidden="true">
                                <img
                                    src={b.icon}
                                    alt=""
                                    draggable={false}
                                    loading="lazy"
                                    className={Classes.watermark}
                                />
                            </div>
                        </motion.article>
                    ))}
                </motion.div>

                <motion.a
                    variants={fadeUp(0.3)}
                    className={Classes.ctaBtn}
                    href="https://book.housecallpro.com/book/Quick-Appliance-Services-and-Dryer-Vent-Cleaning/1d772c9908b14627bf68d004ecf2882d"
                    target="_blank"
                    rel="noreferrer"
                >
                    Book a dryer vent cleaning
                </motion.a>
            </div>
        </motion.section>
    );
}
